import { useState } from 'react';
import { api, type Quiz as QuizType } from '../utils/api';

interface QuizProps {
  userId: string;
  topic: string;
}

export const Quiz = ({ userId, topic }: QuizProps) => {
  const [quiz, setQuiz] = useState<QuizType | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ score: number } | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const generateQuiz = async () => {
    setIsGenerating(true);
    setResult(null);
    setSelectedAnswers([]);
    setCurrentQuestionIndex(0);

    try {
      const newQuiz = await api.generateQuiz(topic, 5);
      setQuiz(newQuiz);
    } catch (error) {
      console.error('Error generating quiz:', error);
      alert('Failed to generate quiz. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (quiz && currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  const handleSubmit = async () => {
    if (!quiz) return;

    setIsSubmitting(true);

    try {
      const response = await api.submitQuiz(userId, quiz.id, selectedAnswers);
      setResult(response);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Failed to submit quiz. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = quiz?.questions[currentQuestionIndex];
  const isAnswered = selectedAnswers[currentQuestionIndex] !== undefined;
  const allAnswered = quiz && selectedAnswers.length === quiz.questions.length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Quiz Mode</h2>
        <p>Test your knowledge about {topic}</p>
      </div>

      {!quiz && !isGenerating && (
        <div style={styles.generateSection}>
          <p style={styles.generateText}>Ready to test your knowledge?</p>
          <button onClick={generateQuiz} style={styles.generateButton}>
            Generate Quiz
          </button>
        </div>
      )}

      {isGenerating && (
        <div style={styles.loadingSection}>
          <div style={styles.loader}></div>
          <p>Generating quiz questions...</p>
        </div>
      )}

      {quiz && !result && currentQuestion && (
        <div style={styles.quizSection}>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${((currentQuestionIndex + 1) / quiz.questions.length) * 100}%`,
              }}
            />
          </div>

          <div style={styles.questionCounter}>
            Question {currentQuestionIndex + 1} of {quiz.questions.length}
          </div>

          <div style={styles.questionText}>{currentQuestion.question}</div>

          <div style={styles.optionsContainer}>
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                style={{
                  ...styles.optionButton,
                  ...(selectedAnswers[currentQuestionIndex] === index
                    ? styles.optionButtonSelected
                    : {}),
                }}
              >
                {String.fromCharCode(65 + index)}. {option}
              </button>
            ))}
          </div>

          {isAnswered && (
            <button
              onClick={() => setShowExplanation(!showExplanation)}
              style={styles.explanationButton}
            >
              {showExplanation ? 'Hide' : 'Show'} Explanation
            </button>
          )}

          {showExplanation && (
            <div style={styles.explanationSection}>
              <div style={styles.explanationLabel}>Correct Answer:</div>
              <div style={styles.explanationText}>
                {String.fromCharCode(65 + currentQuestion.correctAnswer)}.{' '}
                {currentQuestion.options[currentQuestion.correctAnswer]}
              </div>
              <div style={styles.explanationText}>{currentQuestion.explanation}</div>
            </div>
          )}

          <div style={styles.navigationButtons}>
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              style={styles.navButton}
            >
              Previous
            </button>

            {currentQuestionIndex < quiz.questions.length - 1 ? (
              <button onClick={handleNext} style={styles.navButton}>
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!allAnswered || isSubmitting}
                style={styles.submitButton}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            )}
          </div>
        </div>
      )}

      {result && quiz && (
        <div style={styles.resultSection}>
          <h2 style={styles.resultTitle}>Quiz Complete!</h2>
          <div style={styles.scoreDisplay}>
            <div style={styles.scoreLabel}>Your Score</div>
            <div style={styles.scoreValue}>{Math.round(result.score * 100)}%</div>
          </div>

          <div style={styles.resultMessage}>
            {result.score >= 0.9
              ? 'Excellent work! You have mastered this topic!'
              : result.score >= 0.7
              ? 'Great job! You have a good understanding.'
              : 'Keep studying! Review the material and try again.'}
          </div>

          <button onClick={generateQuiz} style={styles.retryButton}>
            Take Another Quiz
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '30px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  generateSection: {
    textAlign: 'center',
    marginTop: '60px',
  },
  generateText: {
    fontSize: '18px',
    marginBottom: '20px',
    color: '#666',
  },
  generateButton: {
    fontSize: '18px',
    padding: '16px 32px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  loadingSection: {
    textAlign: 'center',
    marginTop: '60px',
  },
  loader: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite',
  },
  quizSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    marginBottom: '20px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007bff',
    transition: 'width 0.3s',
  },
  questionCounter: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '16px',
    fontWeight: 'bold',
  },
  questionText: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  optionButton: {
    padding: '16px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '16px',
    transition: 'all 0.2s',
  },
  optionButtonSelected: {
    borderColor: '#007bff',
    backgroundColor: '#e3f2fd',
  },
  explanationButton: {
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  explanationSection: {
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  explanationLabel: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  explanationText: {
    lineHeight: '1.6',
    marginBottom: '8px',
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
  },
  navButton: {
    flex: 1,
    padding: '12px',
    border: '1px solid #007bff',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: '#007bff',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#28a745',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  resultSection: {
    textAlign: 'center',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  resultTitle: {
    marginBottom: '30px',
    color: '#28a745',
  },
  scoreDisplay: {
    marginBottom: '30px',
  },
  scoreLabel: {
    fontSize: '18px',
    color: '#666',
    marginBottom: '10px',
  },
  scoreValue: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#007bff',
  },
  resultMessage: {
    fontSize: '18px',
    marginBottom: '30px',
    color: '#666',
  },
  retryButton: {
    padding: '16px 32px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
};
